import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { departementCourant, peutModifier } from '../services/droits';
import { stageAPI } from '../services/api';
import { getModuleTopbarColor } from './AppSidebar';
import StageAccueil from './StageAccueil';
import StageParametrage from './StageParametrage';
import StageAlternance from './StageAlternance';
import StageDashboard from './StageDashboard';
import StageEvaluations from './StageEvaluations';
import StageAnnuaire from './StageAnnuaire';
import StageDossier from './StageDossier';
import StageCertification from './StageCertification';
import StageParametrageGeneral from './StageParametrageGeneral';
import StageTuteurs from './StageTuteurs';
import StageRechercheEtudiant from './StageRechercheEtudiant';
// Étape A1 de l'aide contextuelle (doc/specs_techniques/aide_contextuelle.md) : le « ? » du bandeau et son volet.
import { AideProvider, useAide } from './aide/AideContexte';
import BoutonAide from './aide/BoutonAide';
import VoletAide from './aide/VoletAide';
import FenetreProcessus from './aide/FenetreProcessus';
import SignalerManque from './aide/SignalerManque';
import AIDE_STAGE from '../aide/stage';
import './SuiviStage.css';

/**
 * MODULE SUIVI DE STAGE — la coquille du module
 * =============================================
 * Spec     : doc/modules/suivi_de_stage.md « Navigation — accueil à vignettes » (E169 → E172)
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (validée par Bertrand et les assistantes le 2026-10-21)
 *
 * ── LOT L1a (E169) : L'ACCUEIL À VIGNETTES REMPLACE LES DEUX NIVEAUX D'ONGLETS ──
 * Les six onglets « populations + ressources » et le niveau 2 « Stagiaires » disparaissent. La barre du haut :
 *   Accueil · Tableau de bord des stages (vue d'ensemble) · Tableau de bord des certifications · [la vignette ouverte ×]
 * L'accueil range des VIGNETTES (StageAccueil) : Outils généraux · Suivi stagiaires / alternants (une par processus)
 * · Certification des documents (une par processus). Une vignette ouverte a ses onglets NUMÉROTÉS :
 *   suivi         Préparer ① Le processus ② Les documents ③ Les courriels · Suivre ④ Tableau de bord ⑤ Dossier ⑥ Évaluation
 *   certification ① Le processus ② Les courriels ③ Les attestations ④ Tableau de bord
 * Tout y est LIMITÉ au processus (les écrans d'avant reçoivent son identifiant et cachent leurs filtres).
 *
 * ── LA RÈGLE D'OUVERTURE EST CALCULÉE PAR LE SERVEUR ───────────────────────
 * `/stage/accueil` rend, pour chaque processus, « ce qui reste à faire » : tant que la liste n'est pas vide, la
 * vignette s'ouvre sur « Le processus », sinon sur le tableau de bord. Jamais une case à cocher.
 *
 * ── CE QUI NE CHANGE PAS EN L1 ─────────────────────────────────────────────
 * Les écrans eux-mêmes (tableau de bord, éditeurs, registre…) ; une formation par processus (L3) ; documents,
 * courriels et attestations communs au département (L2, un bandeau le dit) ; le réglage CFA (L4).
 */

/** Les onglets numérotés d'une vignette de suivi, et le groupe « Préparer » / « Suivre » de chacun. */
const ONGLETS_SUIVI = [
    { cle: 'processus',  n: 1, libelle: 'Le processus',         icone: 'fa-diagram-project', groupe: 'Préparer' },
    { cle: 'documents',  n: 2, libelle: 'Les documents',        icone: 'fa-layer-group' },
    { cle: 'courriels',  n: 3, libelle: 'Les courriels',        icone: 'fa-envelope' },
    { cle: 'tableau',    n: 4, libelle: 'Tableau de bord',      icone: 'fa-table-list', groupe: 'Suivre' },
    { cle: 'dossier',    n: 5, libelle: 'Dossier étudiant',     icone: 'fa-folder-open' },
    { cle: 'evaluation', n: 6, libelle: 'Évaluation stagiaire', icone: 'fa-star-half-stroke' }
];
const ONGLETS_CERTIF = [
    { cle: 'processus',    n: 1, libelle: 'Le processus',     icone: 'fa-diagram-project' },
    { cle: 'modeles',      n: 2, libelle: 'Les courriels',    icone: 'fa-envelope' },
    { cle: 'attestations', n: 3, libelle: 'Les attestations', icone: 'fa-stamp' },
    { cle: 'dash',         n: 4, libelle: 'Tableau de bord',  icone: 'fa-chart-bar' }
];
/** La vue d'ensemble des stages : les trois écrans d'avant, tous processus confondus. */
const ONGLETS_TOUS = [
    { cle: 'tableau',    libelle: 'Tableau de bord',      icone: 'fa-table-list' },
    { cle: 'dossier',    libelle: 'Dossier étudiant',     icone: 'fa-folder-open' },
    { cle: 'evaluation', libelle: 'Évaluation stagiaire', icone: 'fa-star-half-stroke' }
];
const OUTILS = {
    alternance:  { titre: 'Alternance — registre des apprentis', icone: 'fa-handshake',      couleur: '#7c3aed' },
    tuteurs:     { titre: 'Affectation des tuteurs',             icone: 'fa-chalkboard-user', couleur: '#0891b2' },
    entreprises: { titre: 'Annuaire entreprises',                icone: 'fa-building',        couleur: '#b45309' },
    general:     { titre: 'Paramètres généraux',                 icone: 'fa-gears',           couleur: '#475569' }
};
/** Onglet de vignette de suivi → écran du Paramétrage d'avant. */
const ECRAN_PARAM = { processus: 'config', documents: 'modeles', courriels: 'contacts' };
/** Clé d'aide « stagiaires/param/… » → onglet de la vignette de suivi (L1c, « Aller à … » du volet d'aide). */
const ONGLET_DEPUIS_AIDE = { processus: 'processus', modeles: 'documents', courriels: 'courriels' };

/** La racine du module : décalée de la largeur du volet d'aide quand il est ouvert (l'écran reste visible). */
function RacineAidee({ children }) {
    const aide = useAide();
    return <div className={'stg-root' + (aide && aide.ouvert ? ' aide-ouverte' : '')}>{children}</div>;
}

/** « Ce qui reste à faire » en tête de « Le processus » (E169) — la raison de la règle d'ouverture, dite à l'écran. */
/**
 * « Ce qui reste à faire » — UNE ligne (Bertrand, 2026-10-21) : terminé, elle le dit ; sinon elle nomme ce qui manque, et
 * chaque élément est cliquable — il amène au champ (le champ s'allume). Les éléments déjà faits ne s'affichent plus :
 * ils allongeaient la ligne sans rien apprendre.
 */
function ResteAFaire({ v, onAller }) {
    if (!v) return null;
    if (v.pret) {
        return (
            <div className="acc-reste pret ligne">
                <i className="fa-solid fa-circle-check" />
                <div><b>Paramétrage terminé</b> — la vignette s'ouvre sur le tableau de bord. Tout reste modifiable ici
                    (sauf ce qui est figé au premier envoi).</div>
            </div>
        );
    }
    const manque = v.reste.filter((r) => !r.ok);
    return (
        <div className="acc-reste afaire ligne">
            <i className="fa-solid fa-list-check" />
            <div>
                <b>Ce qui reste à faire ({manque.length}) :</b>{' '}
                {manque.map((r, i) => (
                    <React.Fragment key={r.cle}>
                        {i > 0 && <span className="acc-reste-sep"> · </span>}
                        <button type="button" className="acc-reste-lien" onClick={() => onAller && onAller(r.cle)}
                                title="Aller au champ">{r.libelle}</button>
                    </React.Fragment>
                ))}
                <span className="acc-reste-note"> — tant que ce n'est pas fait, la vignette s'ouvre ici.</span>
            </div>
        </div>
    );
}

/** Amène à l'écran le champ d'un élément manquant (`data-reste`), et l'allume un instant. */
function allerAuChamp(cle) {
    const el = document.querySelector(`[data-reste="${cle}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.remove('reste-flash');
    // eslint-disable-next-line no-unused-expressions
    el.offsetWidth;   // relance l'animation
    el.classList.add('reste-flash');
}

function SuiviStageModule({ config, user, anneeId, annees }) {
    const departementId = departementCourant(user, config);
    const modifiable = peutModifier(user, departementId, 'stage');

    /**
     * Où l'on est. `type` : accueil · tous (vue d'ensemble des stages) · certif-tous · suivi · certif · outil.
     * `saut` : une clé neuve à chaque arrivée « avec quelque chose » (recherche pré-remplie, volet, dossier) —
     * l'écran visé repart de zéro, ses valeurs initiales ne se lisent qu'au montage.
     */
    /**
     * L1c — le module se ROUVRE sur le dernier onglet du haut utilisé par ce compte (Accueil, Stages, Certifications) :
     * au quotidien, une assistante va d'abord au tableau de bord. Une vignette n'est pas retenue (elle peut avoir
     * disparu d'une fois sur l'autre). Mémoire du navigateur, clé par compte ; illisible → l'accueil.
     */
    const cleDernier = `stage.dernierOnglet.${user?.id || 'anonyme'}`;
    const vueDuDernier = () => {
        let d = null;
        try { d = localStorage.getItem(cleDernier); } catch (_) { /* sans mémoire : l'accueil */ }
        return d === 'tous' ? { type: 'tous', onglet: 'tableau' } : d === 'certif-tous' ? { type: 'certif-tous' } : { type: 'accueil' };
    };
    const [vue, setVue] = useState(vueDuDernier);
    const [accueil, setAccueil] = useState(null);
    const [erreur, setErreur] = useState(null);

    /**
     * E173 (v85) — LA NOTIFICATION « FICHE MODIFIÉE PAR LE MAÎTRE » : la cloche porte une pastille (les fiches que le
     * maître a modifiées et qui attendent la validation administrative ⑥) et en ouvre la liste. Elle se relit avec
     * l'accueil ; une erreur de lecture la laisse silencieuse (ce n'est qu'une notification).
     */
    const [alertes, setAlertes] = useState([]);
    const [clocheOuverte, setClocheOuverte] = useState(false);
    const refCloche = useRef(null);
    // ⚠️ Aucun rappel de parent dans ces dépendances (leçon du lot 1.3 bis).
    const lireAccueil = useCallback(async () => {
        if (!departementId) return;
        try {
            const r = await stageAPI.getAccueil(departementId, anneeId);
            setAccueil(r.data);
            setErreur(null);
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error || e.message || "Lecture de l'accueil impossible");
        }
        try {
            setAlertes((await stageAPI.getAlertesMaitre(departementId, anneeId)).data.alertes || []);
        } catch (_) { /* silencieuse : la prochaine lecture réessaiera */ }
    }, [departementId, anneeId]);
    useEffect(() => {
        if (!clocheOuverte) return undefined;
        const ailleurs = (e) => { if (refCloche.current && !refCloche.current.contains(e.target)) setClocheOuverte(false); };
        document.addEventListener('mousedown', ailleurs);
        return () => document.removeEventListener('mousedown', ailleurs);
    }, [clocheOuverte]);
    useEffect(() => { lireAccueil(); }, [lireAccueil]);
    /**
     * « Reste à faire » APRÈS un enregistrement (Bertrand) : les champs manquants s'encadrent de rouge, et l'écran va au
     * premier. `signaler` compte les enregistrements de la vignette ouverte (0 = rien à signaler) ; il retombe à 0 quand
     * on change de vignette. Un clic sur un élément de la ligne l'allume aussi.
     */
    const [signaler, setSignaler] = useState(0);
    const ecritEtSignale = useCallback(async () => {
        await lireAccueil();
        setSignaler((n) => n + 1);
    }, [lireAccueil]);
    // Une autre année : retour à l'accueil (les vignettes ouvertes étaient celles de l'année d'avant).
    // ⚠️ Pas au montage : il effacerait le dernier onglet retenu (L1c). On compare à l'année / au département VUS en
    // dernier — le mode strict de React rejoue l'effet deux fois au montage, une garde « premier passage » ne suffit
    // pas (leçon du Dossier étudiant, lot 3.5c-3).
    const vu = useRef(`${departementId}|${anneeId}`);
    useEffect(() => {
        const cle = `${departementId}|${anneeId}`;
        if (vu.current === cle) return;
        const avant = vu.current;
        vu.current = cle;
        // L'année (ou le département) arrive un instant après le montage : ce n'est pas un CHANGEMENT d'année.
        if (avant.split('|').some((x) => x === 'null' || x === 'undefined')) return;
        setVue({ type: 'accueil' });
    }, [anneeId, departementId]);

    const vignetteSuivi = vue.type === 'suivi' ? (accueil?.suivi || []).find((v) => v.id === vue.id) : null;
    useEffect(() => { setSignaler(0); }, [vue.type, vue.id]);
    const vignetteCertif = vue.type === 'certif' ? (accueil?.certif || []).find((v) => v.id === vue.id) : null;
    // Les éléments manquants de la vignette ouverte, une fois signalés (enregistrement, ou clic sur la ligne).
    const vignetteReste = vignetteSuivi || vignetteCertif;
    const manquants = signaler && vignetteReste && !vignetteReste.pret
        ? vignetteReste.reste.filter((r) => !r.ok).map((r) => r.cle) : [];
    // Après un enregistrement : l'écran va au PREMIER champ manquant (l'éditeur se rouvre : on lui laisse le temps).
    const premierManquant = manquants[0] || null;
    useEffect(() => {
        if (!signaler || !premierManquant) return undefined;
        const t = setTimeout(() => allerAuChamp(premierManquant), 350);
        return () => clearTimeout(t);
    }, [signaler, premierManquant]);
    const allerAuManque = useCallback((cle) => { setSignaler((n) => n || 1); allerAuChamp(cle); }, []);

    // ── Les déplacements ─────────────────────────────────────────────────────
    const retourAccueil = useCallback(() => { setVue({ type: 'accueil' }); lireAccueil(); }, [lireAccueil]);
    /** RÈGLE D'OUVERTURE (E169) : « Le processus » tant que ce n'est pas prêt, sinon le tableau de bord. */
    const ouvrirSuivi = useCallback((v, onglet = null) => {
        setVue({ type: 'suivi', id: v.id, onglet: onglet || (v.pret ? 'tableau' : 'processus'), saut: Date.now() });
    }, []);
    const ouvrirCertif = useCallback((v, onglet = null) => {
        setVue({ type: 'certif', id: v.id, onglet: onglet || (v.pret ? 'dash' : 'processus'), saut: Date.now() });
    }, []);
    const ouvrirOutil = useCallback((cle, extra = {}) => setVue({ type: 'outil', cle, saut: Date.now(), ...extra }), []);
    const ouvrirTous = useCallback((onglet = 'tableau', extra = {}) => setVue({ type: 'tous', onglet, saut: Date.now(), ...extra }), []);
    const changerOnglet = (onglet, extra = {}) => setVue((v) => ({ ...v, onglet, saut: Date.now(), ...extra }));

    /**
     * Lot C.1 (CB3) — « Compléter le contact » (certification) ouvre l'écran où corriger, la recherche PRÉ-REMPLIE
     * avec le nom de l'étudiant : le registre des apprentis, ou le tableau de bord de tous les stages.
     */
    const aller = useCallback((vers, recherche) => {
        if (vers === 'alternance') ouvrirOutil('alternance', { recherche });
        else ouvrirTous('tableau', { recherche });
    }, [ouvrirOutil, ouvrirTous]);

    /**
     * L1b — un résultat de la recherche mène où il dit : le DOSSIER de stage dans la vignette de son processus,
     * le tableau de bord de son processus de certification (filtré sur lui), ou le registre des apprentis.
     * Ici, pas de règle d'ouverture : on vient pour CET étudiant.
     */
    const allerAuResultat = useCallback((r) => {
        const nom = `${r.etudiant.nom}`;
        if (r.type === 'stagiaire') setVue({ type: 'suivi', id: r.configuration_id, onglet: 'dossier', dossier: r.dossier_id, saut: Date.now() });
        else if (r.type === 'certification') setVue({ type: 'certif', id: r.processus_id, onglet: 'dash', recherche: nom, saut: Date.now() });
        else ouvrirOutil('alternance', { recherche: nom });
    }, [ouvrirOutil]);

    // ── L'aide : la clé de l'écran ouvert (les écrans internes l'affinent eux-mêmes) et le chemin inverse ──
    const bandeau = useRef(null);
    // Administration → Aide : « Où » ouvre le module sur l'écran signalé, l'aide ouverte (state de navigation).
    const ecranDemande = useLocation().state?.aideEcran || null;
    const AIDE_SUIVI = { tableau: 'stagiaires/tableau', dossier: 'stagiaires/dossier', evaluation: 'stagiaires/evaluation' };
    const cleAide = vue.type === 'accueil' ? 'accueil'
        : vue.type === 'tous' ? AIDE_SUIVI[vue.onglet]
        : vue.type === 'certif-tous' ? 'certification/tableau'
        : vue.type === 'suivi' ? (AIDE_SUIVI[vue.onglet] || 'stagiaires/param')
        : vue.type === 'certif' ? 'certification'
        : vue.cle;
    /**
     * L1c — « Aller à … » depuis le volet d'aide. Dans une vignette ouverte, l'écran demandé est l'onglet de CETTE
     * vignette ; sans vignette, les écrans d'un processus se lisent dans la vue d'ensemble (tableau de bord, dossier,
     * évaluation) — ceux qui n'existent QUE dans une vignette (le processus, les documents…) ne sont pas cliquables :
     * `accessibleAide` dit pourquoi.
     */
    const ouvrirDepuisAide = useCallback((cle) => {
        const [g, s, t] = cle.split('/');
        if (g === 'accueil') { setVue({ type: 'accueil' }); return; }
        if (g === 'stagiaires') {
            const onglet = s === 'param' ? ONGLET_DEPUIS_AIDE[t] : s;
            setVue((v) => (v.type === 'suivi' ? { ...v, onglet, saut: Date.now() }
                : s === 'param' ? v : { type: 'tous', onglet, saut: Date.now() }));
        } else if (g === 'certification') {
            const onglet = s === 'param' ? t : 'dash';
            setVue((v) => (v.type === 'certif' ? { ...v, onglet, saut: Date.now() }
                : s === 'param' ? v : { type: 'certif-tous', saut: Date.now() }));
        } else if (OUTILS[g]) {
            ouvrirOutil(g);
        } else {
            setVue({ type: 'accueil' });
        }
    }, [ouvrirOutil]);
    const typeVue = vue.type;
    const accessibleAide = useCallback((cle) => {
        const [g, s] = cle.split('/');
        if (g === 'stagiaires' && s === 'param' && typeVue !== 'suivi') return "ouvrez d'abord la vignette d'un processus de suivi";
        if (g === 'certification' && s === 'param' && typeVue !== 'certif') return "ouvrez d'abord la vignette d'un processus de certification";
        return null;
    }, [typeVue]);

    const couleurTopbar = getModuleTopbarColor('stage');
    const libelleAnnee = (annees || []).find((a) => a.id === anneeId)?.libelle || '';
    const totaux = accueil?.totaux || {};

    // ── L'onglet de la vignette ouverte, dans la barre du haut ──
    let ongletVignette = null;
    if (vue.type === 'suivi') ongletVignette = { icone: 'fa-diagram-project', couleur: 'var(--stg-vert-d)', titre: vignetteSuivi?.titre || 'Nouveau processus' };
    if (vue.type === 'certif') ongletVignette = { icone: 'fa-stamp', couleur: '#1d4ed8', titre: vignetteCertif?.titre || 'Nouveau processus' };
    if (vue.type === 'outil') ongletVignette = { icone: OUTILS[vue.cle].icone, couleur: OUTILS[vue.cle].couleur, titre: OUTILS[vue.cle].titre };
    const hautActif = vue.type === 'accueil' ? 'accueil' : vue.type === 'tous' ? 'tous' : vue.type === 'certif-tous' ? 'certif-tous' : 'vignette';
    // L1c : retenir le dernier onglet du haut (pas une vignette)
    useEffect(() => {
        if (hautActif === 'vignette') return;
        try { localStorage.setItem(cleDernier, hautActif); } catch (_) { /* sans mémoire : sans effet */ }
    }, [hautActif, cleDernier]);
    const k = vue.saut || 0;

    return (
        <AideProvider donnees={AIDE_STAGE} base={cleAide} departementId={departementId} onOuvrir={ouvrirDepuisAide} ouvrirSur={ecranDemande}
                      accessible={accessibleAide}>
        <RacineAidee>

            {/* ── TOPBAR ─────────────────────────────────────────────────── */}
            <div
                ref={bandeau}
                className="stg-topbar aide-garde-largeur"
                style={{ background: `linear-gradient(135deg, ${couleurTopbar} 0%, ${couleurTopbar}cc 100%)` }}
            >
                <div className="stg-topbar-left">
                    <i className="fa-solid fa-building stg-topbar-icon" />
                    <div>
                        <div className="stg-topbar-title">Suivi stages / alternance</div>
                        <div className="stg-topbar-sub">
                            {config?.departement?.nom || config?.nom || 'Département'}
                            {libelleAnnee ? ' · ' + libelleAnnee : ''}
                        </div>
                    </div>
                </div>
                <span style={{ flex: 1 }} />
                {/* « Mes notifications » est dans Paramètres généraux ; la cloche y mène (une icône seule ne se trouvait pas).
                    E173 : elle porte aussi la NOTIFICATION des fiches modifiées par le maître (pastille + liste). */}
                <span className="stg-cloche" ref={refCloche}>
                    <button type="button" className="stg-tb-btn" aria-expanded={clocheOuverte}
                            title={alertes.length ? `${alertes.length} fiche${alertes.length > 1 ? 's' : ''} modifiée${alertes.length > 1 ? 's' : ''} par le maître — et vos notifications`
                                                  : 'Notifications'}
                            onClick={() => setClocheOuverte((o) => !o)}>
                        <i className="fa-solid fa-bell" />
                        {alertes.length > 0 && <span className="stg-cloche-n">{alertes.length}</span>}
                    </button>
                    {clocheOuverte && (
                        <div className="stg-cloche-pop" role="dialog" aria-label="Notifications">
                            <div className="stg-cloche-titre"><i className="fa-solid fa-pen" /> Fiches modifiées par le maître ({alertes.length})</div>
                            {!alertes.length && <div className="stg-cloche-vide">Aucune fiche modifiée par le maître n'attend la validation administrative.</div>}
                            {alertes.map((a) => (
                                <button type="button" key={a.dossier_id} className="stg-cloche-l"
                                        onClick={() => { setClocheOuverte(false); setVue({ type: 'suivi', id: a.configuration_id, onglet: 'tableau', dossier: a.dossier_id, saut: Date.now() }); }}>
                                    <b>{a.etudiant}</b>{a.entreprise ? ` — ${a.entreprise}` : ''}
                                    <span>{a.processus} · {a.champs.join(', ')}{a.le ? ` · le ${new Date(a.le).toLocaleDateString('fr-FR')}` : ''}</span>
                                </button>
                            ))}
                            <div className="stg-cloche-pied">
                                <span>La validation administrative (⑥) reste à l'assistante ; les responsables des stages ont reçu un courriel.</span>
                                <button type="button" className="stg-lien-processus"
                                        onClick={() => { setClocheOuverte(false); ouvrirOutil('general', { ecranGeneral: 'notifications' }); }}>
                                    Mes notifications (récapitulatif quotidien)
                                </button>
                            </div>
                        </div>
                    )}
                </span>
                <BoutonAide />
            </div>

            {/* ── LA BARRE DU HAUT (toujours visible) ────────────────────── */}
            <div className="stg-tabs acc-haut" role="tablist" aria-label="Navigation du module">
                <button type="button" role="tab" aria-selected={hautActif === 'accueil'} className={'stg-tab' + (hautActif === 'accueil' ? ' active' : '')}
                        onClick={retourAccueil}>
                    <i className="fa-solid fa-house" /> Accueil
                </button>
                <button type="button" role="tab" aria-selected={hautActif === 'tous'} className={'stg-tab' + (hautActif === 'tous' ? ' active' : '')}
                        onClick={() => ouvrirTous()}>
                    <i className="fa-solid fa-table-list" />
                    <span className={ongletVignette ? 'acc-lib-court' : ''}>{ongletVignette ? 'Stages' : 'Tableau de bord des stages'}</span>
                    {totaux.suivi_a_valider > 0 && <span className="acc-onglet-compte">{totaux.suivi_a_valider} à valider</span>}
                </button>
                <button type="button" role="tab" aria-selected={hautActif === 'certif-tous'} className={'stg-tab' + (hautActif === 'certif-tous' ? ' active' : '')}
                        onClick={() => setVue({ type: 'certif-tous', saut: Date.now() })}>
                    <i className="fa-solid fa-stamp" />
                    <span>{ongletVignette ? 'Certifications' : 'Tableau de bord des certifications'}</span>
                    {totaux.certif_a_traiter > 0 && <span className="acc-onglet-compte">{totaux.certif_a_traiter} à traiter</span>}
                </button>
                {ongletVignette && (
                    <div role="tab" aria-selected="true" className="stg-tab active acc-onglet-vignette">
                        <i className={'fa-solid ' + ongletVignette.icone} style={{ color: ongletVignette.couleur }} />
                        <span className="acc-onglet-titre" title={ongletVignette.titre}>{ongletVignette.titre}</span>
                        <button type="button" className="acc-onglet-fermer" title="Fermer et revenir à l'accueil" onClick={retourAccueil}>×</button>
                    </div>
                )}
                {/* L1b : rechercher un étudiant dans tout le module — le résultat mène à SON dossier. */}
                <StageRechercheEtudiant departementId={departementId} anneeId={anneeId} onChoisir={allerAuResultat} />
            </div>

            {/* ── LES ONGLETS DE LA VIGNETTE (ou de la vue d'ensemble) ──── */}
            {vue.type === 'suivi' && (
                <div className="stg-subtabs" role="tablist" aria-label="Onglets du processus">
                    {ONGLETS_SUIVI.map((o) => (
                        <React.Fragment key={o.cle}>
                            {o.groupe && <span className={'acc-sep-groupe' + (o.n > 1 ? ' trait' : '')}>{o.groupe}</span>}
                            <button type="button" role="tab" aria-selected={vue.onglet === o.cle}
                                    className={'stg-subtab' + (vue.onglet === o.cle ? ' active' : '')} onClick={() => changerOnglet(o.cle)}>
                                <span className="acc-num">{o.n}</span><i className={'fa-solid ' + o.icone} /> {o.libelle}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            )}
            {vue.type === 'certif' && (
                <div className="stg-subtabs" role="tablist" aria-label="Onglets du processus de certification">
                    {ONGLETS_CERTIF.map((o) => (
                        <button key={o.cle} type="button" role="tab" aria-selected={vue.onglet === o.cle}
                                className={'stg-subtab' + (vue.onglet === o.cle ? ' active' : '')} onClick={() => changerOnglet(o.cle)}>
                            <span className="acc-num">{o.n}</span><i className={'fa-solid ' + o.icone} /> {o.libelle}
                        </button>
                    ))}
                </div>
            )}
            {vue.type === 'tous' && (
                <div className="stg-subtabs" role="tablist" aria-label="Vue d'ensemble des stages">
                    {ONGLETS_TOUS.map((o) => (
                        <button key={o.cle} type="button" role="tab" aria-selected={vue.onglet === o.cle}
                                className={'stg-subtab' + (vue.onglet === o.cle ? ' active' : '')} onClick={() => changerOnglet(o.cle)}>
                            <i className={'fa-solid ' + o.icone} /> {o.libelle}
                        </button>
                    ))}
                </div>
            )}

            {/* ── CONTENU ────────────────────────────────────────────────── */}
            <div className="stg-content">
                {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

                {vue.type === 'accueil' && (
                    <StageAccueil donnees={accueil} departementId={departementId} anneeId={anneeId} modifiable={modifiable}
                                  onOuvrirSuivi={ouvrirSuivi} onOuvrirCertif={ouvrirCertif} onOutil={ouvrirOutil} onRecharger={lireAccueil}
                                  userId={user?.id} />
                )}

                {/* La vue d'ensemble des stages : les écrans d'avant, tous processus confondus. */}
                {vue.type === 'tous' && vue.onglet === 'tableau' && (
                    <StageDashboard key={'tous' + k} config={config} user={user} anneeId={anneeId} rechercheInitiale={vue.recherche || ''}
                                    onVoirEvaluation={(dossierId) => changerOnglet('evaluation', { volet: dossierId })}
                                    vueEnsemble processusListe={accueil?.suivi || []}
                                    onOuvrirProcessus={(id) => { const v = (accueil?.suivi || []).find((x) => x.id === id); if (v) ouvrirSuivi(v); }} />
                )}
                {vue.type === 'tous' && vue.onglet === 'dossier' && (
                    <StageDossier key={'tous' + k} config={config} user={user} anneeId={anneeId} dossierInitial={vue.dossier || null} />
                )}
                {vue.type === 'tous' && vue.onglet === 'evaluation' && (
                    <StageEvaluations key={'tous' + k} config={config} user={user} anneeId={anneeId} voletInitial={vue.volet || null}
                                      onParametrage={retourAccueil} onOuvrirDossier={(l) => changerOnglet('dossier', { dossier: l.id })} />
                )}
                {vue.type === 'certif-tous' && (
                    <StageCertification key={'ct' + k} config={config} user={user} anneeId={anneeId} onAller={aller} onglet="dash" vueEnsemble />
                )}

                {/* ── Une vignette de SUIVI : tout est limité à CE processus ── */}
                {vue.type === 'suivi' && vue.onglet === 'processus' && <ResteAFaire v={vignetteSuivi} onAller={allerAuManque} />}
                {vue.type === 'suivi' && ECRAN_PARAM[vue.onglet] && (
                    <StageParametrage key={vue.id + vue.onglet} config={config} user={user} anneeId={anneeId}
                                      ecran={ECRAN_PARAM[vue.onglet]} configurationId={vue.id} manquants={manquants}
                                      onEcrit={vue.onglet === 'processus' ? ecritEtSignale : lireAccueil} onSupprime={retourAccueil} onEquipe={() => ouvrirOutil('general')} />
                )}
                {vue.type === 'suivi' && vue.onglet === 'tableau' && vignetteSuivi && (
                    <StageDashboard key={vue.id + 't' + k} config={config} user={user} anneeId={anneeId}
                                    processus={{ id: vignetteSuivi.id }} dossierInitial={vue.dossier || null}
                                    onVoirEvaluation={(dossierId) => changerOnglet('evaluation', { volet: dossierId })} />
                )}
                {vue.type === 'suivi' && vue.onglet === 'dossier' && (
                    <StageDossier key={vue.id + 'd' + k} config={config} user={user} anneeId={anneeId} configurationId={vue.id} dossierInitial={vue.dossier || null} />
                )}
                {vue.type === 'suivi' && vue.onglet === 'evaluation' && (
                    <StageEvaluations key={vue.id + 'e' + k} config={config} user={user} anneeId={anneeId} configurationId={vue.id}
                                      voletInitial={vue.volet || null} onParametrage={() => changerOnglet('processus')}
                                      onOuvrirDossier={(l) => changerOnglet('dossier', { dossier: l.id })} />
                )}

                {/* ── Une vignette de CERTIFICATION ── */}
                {vue.type === 'certif' && vue.onglet === 'processus' && <ResteAFaire v={vignetteCertif} onAller={allerAuManque} />}
                {vue.type === 'certif' && (
                    <StageCertification key={vue.id + vue.onglet + k} config={config} user={user} anneeId={anneeId} onAller={aller}
                                        onglet={vue.onglet} processusId={vue.id} manquants={manquants}
                                        onEcrit={vue.onglet === 'processus' ? ecritEtSignale : lireAccueil} onSupprime={retourAccueil}
                                        rechercheInitiale={vue.recherche || ''} />
                )}

                {/* ── Les OUTILS GÉNÉRAUX ── */}
                {vue.type === 'outil' && vue.cle === 'alternance' && (
                    <StageAlternance key={'alt' + k} config={config} user={user} anneeId={anneeId} rechercheInitiale={vue.recherche || ''} />
                )}
                {vue.type === 'outil' && vue.cle === 'tuteurs' && <StageTuteurs config={config} user={user} anneeId={anneeId}
                    onOuvrirProcessus={(id) => { const v = (accueil?.suivi || []).find((x) => x.id === id); if (v) ouvrirSuivi(v); }} />}
                {vue.type === 'outil' && vue.cle === 'entreprises' && <StageAnnuaire config={config} user={user} />}
                {vue.type === 'outil' && vue.cle === 'general' && (
                    <StageParametrageGeneral key={'gen' + k} config={config} user={user} anneeId={anneeId} ecranInitial={vue.ecranGeneral || 'equipe'} />
                )}
            </div>
            <VoletAide ancre={bandeau} />
            <FenetreProcessus />
            <SignalerManque />
        </RacineAidee>
        </AideProvider>
    );
}

export default SuiviStageModule;
